import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [department, setDepartment] = useState("support");
  const [priority, setPriority] = useState("medium");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/tickets").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/tickets", { subject, body, department, priority });
      toast.success("Ticket criado");
      setSubject(""); setBody(""); setDepartment("support"); setPriority("medium"); setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  const badge = (s) => {
    const map = {
      open: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      answered: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      closed: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
    };
    return <Badge className={`rounded-sm border ${map[s]}`}>{s}</Badge>;
  };

  return (
    <div className="space-y-8" data-testid="tickets-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// suporte</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Tickets</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp" data-testid="new-ticket-btn"><Plus className="h-4 w-4 mr-2" /> Novo ticket</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-[#121214] border-white/10 max-w-lg" data-testid="new-ticket-dialog">
            <DialogHeader><DialogTitle className="font-heading font-bold tracking-tighter">Abrir novo ticket</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Assunto</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} required className="rounded-sm bg-[#09090B] border-white/10 mt-1" data-testid="ticket-subject-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Departamento</Label>
                  <Select value={department} onValueChange={setDepartment}>
                    <SelectTrigger className="rounded-sm bg-[#09090B] border-white/10 mt-1" data-testid="ticket-department-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121214] border-white/10">
                      <SelectItem value="support">Suporte técnico</SelectItem>
                      <SelectItem value="billing">Financeiro</SelectItem>
                      <SelectItem value="sales">Comercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Prioridade</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className="rounded-sm bg-[#09090B] border-white/10 mt-1" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121214] border-white/10">
                      <SelectItem value="low">Baixa</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Mensagem</Label>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={5} className="rounded-sm bg-[#09090B] border-white/10 mt-1" data-testid="ticket-body-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp" data-testid="ticket-submit-btn">
                {loading ? "Enviando…" : "Abrir ticket"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-white/10 bg-[#121214]">
        {items.length === 0 ? (
          <div className="p-10 text-center text-zinc-500">Nenhum ticket ainda.</div>
        ) : (
          <ul className="divide-y divide-white/10">
            {items.map((t) => (
              <li key={t.ticket_id} className="p-5 hover:bg-white/5">
                <Link to={`/dashboard/tickets/${t.ticket_id}`} className="flex items-center justify-between" data-testid={`ticket-list-row-${t.ticket_id}`}>
                  <div>
                    <div className="font-medium">{t.subject}</div>
                    <div className="text-xs text-zinc-500 font-mono mt-1">{t.department} · prioridade {t.priority} · {fmtDate(t.updated_at)}</div>
                  </div>
                  {badge(t.status)}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
